import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, Loader2, Brain, FileText } from 'lucide-react';

const SemanticSearchTab = ({
  searchQuery, setSearchQuery,
  searchRunning, searchResult,
  onRunSearch, onOpenIndividual,
}) => (
  <Card className="border-[#E5E7EB]">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm flex items-center gap-2"><Search className="w-4 h-4 text-[#007AFF]" />Semantic Search</CardTitle>
      <p className="text-xs text-[#6B7280] mt-1">
        Ask a natural-language question. GPT-5.2 translates it into a structured ontology query, executes it against your classes + relations + individuals, and returns grounded answers with linked PKM notes.
      </p>
    </CardHeader>
    <CardContent className="space-y-3">
      <div className="flex gap-2">
        <Input
          className="flex-1"
          placeholder='e.g. "Show me all Methodologies implemented by support teams"'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onRunSearch(); }}
          data-testid="search-query-input"
        />
        <Button className="bg-[#002FA7] hover:bg-[#001F6F]" onClick={onRunSearch} disabled={searchRunning} data-testid="run-search">
          {searchRunning && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
          {!searchRunning && <Search className="w-4 h-4 mr-1.5" />}
          Ask
        </Button>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="search-suggestions">
        {[
          'Show me all classes with no individuals',
          'Which individuals are linked to PKM notes?',
          'Find anything related to KCS',
        ].map(s => (
          <button key={s} type="button" className="text-[10px] px-2 py-1 rounded-full bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#4B5563]" onClick={() => { setSearchQuery(s); }}>
            {s}
          </button>
        ))}
      </div>

      {searchResult && (
        <div className="space-y-3 pt-2" data-testid="search-result">
          {/* Plan explanation */}
          <div className="p-3 rounded-lg bg-[#F0F9FF] border border-[#BAE6FD]">
            <p className="text-xs font-medium text-[#0369A1] mb-1 flex items-center gap-1"><Brain className="w-3 h-3" />Query Plan</p>
            <p className="text-xs text-[#0C4A6E]">{searchResult.explanation || 'No explanation provided.'}</p>
            {searchResult.plan && (
              <div className="flex flex-wrap gap-1 mt-2">
                {(searchResult.plan.target_class_names || []).map(cn => (
                  <Badge key={cn} className="text-[9px] bg-[#002FA7]/10 text-[#002FA7]">class: {cn}</Badge>
                ))}
                {(searchResult.plan.via_relations || []).map((v, i) => (
                  <Badge key={i} className="text-[9px] bg-[#AF52DE]/10 text-[#AF52DE]">
                    {v.direction === 'incoming' ? '←' : '→'} {v.relation_name}{v.counterpart_class_name ? ' : ' + v.counterpart_class_name : ''}
                  </Badge>
                ))}
                {(searchResult.plan.name_keywords || []).map(k => (
                  <Badge key={k} variant="secondary" className="text-[9px]">kw: {k}</Badge>
                ))}
              </div>
            )}
          </div>

          {/* Results */}
          <div>
            <p className="text-xs font-semibold mb-2">Results ({searchResult.total})</p>
            {searchResult.total === 0 && (
              <p className="text-xs text-[#9CA3AF] p-4 text-center border border-dashed border-[#E5E7EB] rounded-lg">
                No individuals match this query. Try creating more classes, individuals, or triples first.
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {searchResult.results.map(r => (
                <div
                  key={r.individual_id}
                  className="p-3 rounded-lg border border-[#E5E7EB] hover:border-[#002FA7] cursor-pointer"
                  onClick={() => onOpenIndividual(r)}
                  data-testid={`search-hit-${r.individual_id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: r.class_color }} />
                    <span className="font-medium text-sm text-[#111827]">{r.name}</span>
                    <Badge className="text-[9px]" style={{ backgroundColor: r.class_color + '20', color: r.class_color }}>{r.class_name}</Badge>
                  </div>
                  {r.description && <p className="text-[10px] text-[#6B7280] line-clamp-2">{r.description}</p>}
                  {r.linked_notes?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {r.linked_notes.slice(0, 3).map(n => (
                        <Badge key={n.note_id} variant="secondary" className="text-[9px]">
                          <FileText className="w-2.5 h-2.5 mr-0.5" />{n.title}
                        </Badge>
                      ))}
                      {r.linked_notes.length > 3 && <Badge variant="secondary" className="text-[9px]">+{r.linked_notes.length - 3}</Badge>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </CardContent>
  </Card>
);

export default SemanticSearchTab;
